import React, { useState } from 'react';
import { authAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const ChangePassword = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
        if (errors[id]) {
            setErrors({ ...errors, [id]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        // Validate passwords match
        if (formData.new_password !== formData.new_password_confirmation) {
            setErrors({ new_password_confirmation: 'Passwords do not match' });
            toast.error('New passwords do not match', {
                position: "top-right",
                autoClose: 4000,
                theme: "colored"
            });
            setLoading(false);
            return;
        }

        // Validate password length
        if (formData.new_password.length < 6) {
            setErrors({ new_password: 'Password must be at least 6 characters' });
            toast.error('Password must be at least 6 characters', {
                position: "top-right",
                autoClose: 4000,
                theme: "colored"
            });
            setLoading(false);
            return;
        }

        try {
            const response = await authAPI.changePassword({
                current_password: formData.current_password,
                new_password: formData.new_password,
                new_password_confirmation: formData.new_password_confirmation
            });

            if (response.data.success) {
                toast.success('Password changed successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });

                // Reset form
                setFormData({
                    current_password: '',
                    new_password: '',
                    new_password_confirmation: ''
                });

                // Optional: Logout after password change
                setTimeout(() => {
                    if (window.confirm('Password changed successfully. Do you want to login again?')) {
                        localStorage.clear();
                        navigate('/login');
                    }
                }, 1000);
            }
        } catch (error) {
            console.error('Password change error:', error);

            // Handle 401 Unauthorized - Current password incorrect
            if (error.response?.status === 401) {
                const errorMessage = error.response?.data?.message || 'Current password is incorrect';
                setErrors({ current_password: errorMessage });
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 4000,
                    theme: "colored"
                });
            }
            // Handle 422 Validation errors
            else if (error.response?.status === 422 && error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                const firstError = Object.values(error.response.data.errors)[0];
                toast.error(firstError[0], {
                    position: "top-right",
                    autoClose: 4000,
                    theme: "colored"
                });
            }
            // Handle other errors
            else {
                const errorMessage = error.response?.data?.message || 'Failed to change password';
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 4000,
                    theme: "colored"
                });

                // If the error message indicates current password issue
                if (errorMessage.toLowerCase().includes('current password') ||
                    errorMessage.toLowerCase().includes('incorrect')) {
                    setErrors({ current_password: errorMessage });
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="main-content">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="page-header">
                <h1 className="page-title">
                    <i className="fas fa-key me-2"></i> Change Password
                </h1>
                <p className="page-subtitle">Update your account password</p>
            </div>

            <div className="content-wrapper">
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <div className="change-password-card">
                            <div className="card-header-custom">
                                <h5><i className="fas fa-lock me-2"></i> Password Settings</h5>
                                <p className="text-muted small mb-0">Ensure your account is secure with a strong password</p>
                            </div>
                            <div className="card-body-custom">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label required-field">Current Password</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-lock"></i>
                                            </span>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                id="current_password"
                                                className={`form-control ${errors.current_password ? 'is-invalid' : ''}`}
                                                value={formData.current_password}
                                                onChange={handleChange}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={togglePasswordVisibility}
                                            >
                                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.current_password && <div className="invalid-feedback">{errors.current_password[0]}</div>}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label required-field">New Password</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-key"></i>
                                            </span>
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                id="new_password"
                                                className={`form-control ${errors.new_password ? 'is-invalid' : ''}`}
                                                value={formData.new_password}
                                                onChange={handleChange}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={toggleNewPassword}
                                            >
                                                <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.new_password && <div className="invalid-feedback">{errors.new_password[0]}</div>}
                                        <small className="form-text text-muted">Password must be at least 6 characters</small>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label required-field">Confirm New Password</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-check-circle"></i>
                                            </span>
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                id="new_password_confirmation"
                                                className={`form-control ${errors.new_password_confirmation ? 'is-invalid' : ''}`}
                                                value={formData.new_password_confirmation}
                                                onChange={handleChange}
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={toggleConfirmPassword}
                                            >
                                                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.new_password_confirmation && <div className="invalid-feedback">{errors.new_password_confirmation[0]}</div>}
                                    </div>

                                    <div className="password-requirements">
                                        <h6>Password Requirements:</h6>
                                        <ul>
                                            <li>Minimum 6 characters</li>
                                            <li>Use a mix of letters and numbers</li>
                                            <li>Avoid common passwords</li>
                                        </ul>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between">
                                        <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>
                                            <i className="fas fa-arrow-left me-2"></i> Back
                                        </button>
                                        <button type="submit" className="btn-submit" disabled={loading}>
                                            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-save'} me-2`}></i>
                                            {loading ? 'Changing...' : 'Change Password'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;